import { useMemo } from "react";
import en from "@/i18n/sso-locales/en.json";
import ja from "@/i18n/sso-locales/ja.json";
import ko from "@/i18n/sso-locales/ko.json";
import ar from "@/i18n/sso-locales/ar.json";
import ptBr from "@/i18n/sso-locales/pt-br.json";
import es from "@/i18n/sso-locales/es.json";
import de from "@/i18n/sso-locales/de.json";
import fr from "@/i18n/sso-locales/fr.json";
import zhHk from "@/i18n/sso-locales/zh-hk.json";
import hi from "@/i18n/sso-locales/hi.json";

export interface SsoLocaleCatalog {
  locale: string;
  dir: "ltr" | "rtl";
  /** SPEC-SSO-7 §5: `en` is authored; the other nine ship machine-translated
   * and are marked so a reviewer knows which catalogues still need a human
   * pass. The marker is metadata only — never rendered. */
  review?: "human" | "machine";
  ui: Record<string, string>;
}

/**
 * Locale catalog for the Single sign-on surfaces (ALL-4870 / SPEC-SSO-7 §5
 * "all strings go through the shared catalogues, and every supported language
 * ships in the same PR"; D150). Mirrors the shape and loading pattern of
 * src/i18n/use-hot-wallets-locale.ts rather than introducing a second
 * convention.
 *
 * Moved here from src/features/admin/sso/ when the public login panel
 * (ALL-5464) became the second consumer: the catalogues have always lived under
 * src/i18n/sso-locales/ beside the other shared ones, so the loader now sits
 * with them — the same split src/i18n/use-error-locale.ts already uses — and
 * neither the admin page nor the login page imports across the other's feature
 * directory. The keys are namespaced (`sso.` for the admin surface, `login.sso.`
 * for the login panel) so one catalogue serves both without collisions.
 */
export const SSO_LOCALES: Record<string, SsoLocaleCatalog> = {
  en: en as SsoLocaleCatalog,
  ja: ja as SsoLocaleCatalog,
  ko: ko as SsoLocaleCatalog,
  ar: ar as SsoLocaleCatalog,
  "pt-br": ptBr as SsoLocaleCatalog,
  es: es as SsoLocaleCatalog,
  de: de as SsoLocaleCatalog,
  fr: fr as SsoLocaleCatalog,
  "zh-hk": zhHk as SsoLocaleCatalog,
  hi: hi as SsoLocaleCatalog,
};

export const DEFAULT_SSO_LOCALE = "en";

/** Resolves the active locale: an explicit `?locale=` query param (used by
 * Playwright to cover 2+ locales deterministically) takes priority over the
 * browser's language, which takes priority over English. */
export function resolveSsoLocale(searchParams: URLSearchParams): string {
  const requested = searchParams.get("locale");
  if (requested && SSO_LOCALES[requested]) return requested;

  if (typeof navigator !== "undefined") {
    const nav = navigator.language?.toLowerCase();
    if (nav && SSO_LOCALES[nav]) return nav;
    const short = nav?.split("-")[0];
    if (short && SSO_LOCALES[short]) return short;
  }
  return DEFAULT_SSO_LOCALE;
}

/** Reads `?locale=` from the current URL (or an empty query string outside
 * the browser) and returns the matching catalog, memoized on that query
 * string. */
export function useSsoLocale(): SsoLocaleCatalog {
  const search = typeof window !== "undefined" ? window.location.search : "";
  return useMemo(() => {
    const code = resolveSsoLocale(new URLSearchParams(search));
    return SSO_LOCALES[code] ?? SSO_LOCALES[DEFAULT_SSO_LOCALE];
  }, [search]);
}

/** Fills `{name}`-style placeholders in a localized template. */
export function formatTemplate(template: string, values: Record<string, string>): string {
  let out = template;
  for (const [key, value] of Object.entries(values)) {
    out = out.split(`{${key}}`).join(value);
  }
  return out;
}
